import { GoogleMap, InfoWindow, Marker, MarkerClusterer } from '@react-google-maps/api';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as AlertIcon } from '../../assets/icons/alert-icon.svg';
import AlertStationIcon from '../../assets/icons/dashboard/alert-station-locator.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/dashboard/right-arrow.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/dashboard/user-icon.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import { useAppBranding, metaValues } from '../../config/branding';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import dashboardApi from '../../utils/api/dashboard';
import AutoCompleteSelectField from '../auto-complete-select-field/AutoCompleteSelectField';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';

interface StationProperties {
  id: string;
  position: { lat: number; lng: number };
  alert: boolean;
  name: string;
  address: string;
}

/**
 * Validates the position of a station.
 * @param {object} position - The position object.
 * @returns {object | null} The validated position or null if invalid.
 */
const validatePosition = (position: { lat: any; lng: any }) => {
  const lat = parseFloat(position.lat);
  const lng = parseFloat(position.lng);
  return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
};

interface CustomDashboardMapProperties {
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
  isWidget?: boolean;
  viewonly?: boolean;
  staticData?: any;
  showSearch?: boolean;
}

/**
 * Shared Map component for Dashboard and Custom Widgets
 */
function CustomDashboardMap({
  filters,
  isWidget = false,
  viewonly = false,
  staticData,
  showSearch = false,
}: CustomDashboardMapProperties) {
  const branding = useAppBranding();
  const { t } = useTranslation('dashboard');
  const { isLoaded } = useGoogleMaps();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [selectedMarker, setSelectedMarker] = useState<StationProperties | null>(null);
  const [stationLocation, setStationLocation] = useState<StationProperties[]>([]);
  const [singleStationDetails, setSingleStationDetails] = useState<any>(null);
  const [searchStation, setSearchStation] = useState<string | number>('');
  const [center, setCenter] = useState({
    lat: metaValues.latLong.lat,
    lng: metaValues.latLong.lng,
  });

  /**
   * Fetches the list of all charging stations based on filters.
   * @param {object} filterParameters - Filters for stations.
   */
  const fetchAllChargingStationList = (filterParameters: any = {}) => {
    const { facilityIds, stationIds } = filterParameters;
    dashboardApi
      .fetchAllChargingStationList({
        accountCustomId: facilityIds,
        stationIds,
      })
      .then((response) => {
        const stationList: StationProperties[] = response?.data?.map((station: any) => ({
          id: station._id,
          position: { lat: station.latitude, lng: station.longitude },
          alert: station.isAlert,
          name: station.name,
          address: station.address,
        }));
        setStationLocation(stationList.filter((station) => station.address !== undefined));
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handles fetching details for a single station.
   * @param {StationProperties} marker - The station marker properties.
   */
  const handleFetchSingleStation = async (marker: StationProperties) => {
    const validPosition = validatePosition(marker.position);
    if (validPosition) {
      setCenter(validPosition);
      setSelectedMarker({ ...marker, position: validPosition });
      dashboardApi
        .getChargingStationDetails(marker.id)
        .then((response) => {
          if (response.data.length) {
            setSingleStationDetails(response.data?.[0]);
          }
        })
        .catch((error) => {
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * Filters stations by their details (name).
   * @param {string} name - Parameter name.
   * @param {string | number} value - The search value.
   */
  const handleFilterByStationDetails = (name: string, value: string | number) => {
    setSearchStation(value);
    const matchedStation = stationLocation.find((station) => station.name === value);
    if (matchedStation) {
      handleFetchSingleStation(matchedStation);
    }
  };

  useEffect(() => {
    if (viewonly || !isWidget) {
      fetchAllChargingStationList(filters || {});
    } else {
      // Customization mode
      const mockStations =
        staticData?.markers?.map((m: any) => ({
          id: m.id,
          position: { lat: m.lat, lng: m.lng },
          alert: m.status === 'maintenance' || m.status === 'busy',
          name: m.name || 'Station',
          address: m.address || '',
        })) || [];
      setStationLocation(mockStations);
    }
  }, [filters, viewonly, isWidget, staticData]);

  if (!isLoaded) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <BatteryLoader />
      </div>
    );
  }

  const mapCenter = viewonly || !isWidget ? center : staticData?.center || center;
  const mapZoom = viewonly || !isWidget ? 7 : staticData?.zoom || 10;

  return (
    <div
      className={`custom-dashboard-map-container ${isWidget ? 'is-widget' : ''}`}
      style={{ width: '100%', height: '100%', position: 'relative' }}>
      {showSearch && (
        <div
          className="map-search-wrap"
          style={{ position: 'absolute', top: '10px', left: '130px', zIndex: 5, width: '250px' }}>
          <AutoCompleteSelectField
            inputLabel=""
            placeholder="Search"
            name="search"
            selectedValue={searchStation}
            onChangeValue={handleFilterByStationDetails}
            svgIcon={<SearchIcon />}
            options={stationLocation.map((station) => ({
              label: `${station.name} (${station.address})`,
              value: station.name,
            }))}
          />
        </div>
      )}

      <GoogleMap
        mapContainerStyle={{
          width: '100%',
          height: '100%',
          borderRadius: isWidget ? '8px' : '0px',
        }}
        center={mapCenter}
        zoom={mapZoom}>
        <MarkerClusterer>
          {(clusterer) => (
            <>
              {stationLocation.map((marker) => {
                const validPos = validatePosition(marker.position);
                return (
                  validPos && (
                    <Marker
                      key={marker.id}
                      position={validPos}
                      clusterer={clusterer}
                      icon={
                        marker.alert
                          ? {
                              url: AlertStationIcon,
                              scaledSize: new window.google.maps.Size(76, 47),
                            }
                          : {
                              url: branding.icons.stationLocator,
                              scaledSize: new window.google.maps.Size(44, 44),
                            }
                      }
                      onClick={() => (viewonly || !isWidget) && handleFetchSingleStation(marker)}
                    />
                  )
                );
              })}
            </>
          )}
        </MarkerClusterer>

        {selectedMarker && (viewonly || !isWidget) && (
          <InfoWindow
            position={selectedMarker.position}
            onCloseClick={() => setSelectedMarker(null)}>
            {(() => {
              if (selectedMarker.alert) {
                return (
                  <div className="map-station-details-wrap alert-wrap">
                    <div className="map-station-details-container">
                      <span>
                        <AlertIcon />
                      </span>
                      <p>{t('stationPerformanceAlerts')}</p>
                    </div>
                  </div>
                );
              }
              if (singleStationDetails) {
                return (
                  <div className="map-station-active-container">
                    <div className="map-station-details">
                      <div className="details-header">
                        <div className="details-header-image-wrap">
                          <img
                            src={branding.images.chargingDevice}
                            alt=""
                            className="details-header-image"
                          />
                        </div>
                        <div className="name-and-status">
                          <h2>{singleStationDetails.name}</h2>
                          {singleStationDetails.isAvailable ? (
                            <div className="status-wrap active">
                              <div className="status-point" />
                              <p>{t('active')}</p>
                            </div>
                          ) : (
                            <div className="status-wrap in-active">
                              <div className="status-point in-active" />
                              <p>{t('inactive')}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="address-details">
                        <div className="place-with-icon">
                          <span />
                          <span>
                            <LocationIcon />
                          </span>
                        </div>
                        <p>{singleStationDetails.address}</p>
                      </div>
                      <div className="user-details">
                        <span>
                          <UserIcon />
                        </span>
                        <span className="count">{singleStationDetails.clientsOnStation}</span>
                        <span className="message">{t('clientOnStation')}</span>
                      </div>
                    </div>
                    <div className="map-station-center-border" />
                    <div className="map-station-availability-wrap">
                      <div className="availability-status-wrap">
                        <div className="each-availability">
                          <span>
                            <branding.icons.dashboardClock />
                          </span>
                          <p>{`${ConvertUTCtoHHMM(singleStationDetails.opensAt)} - ${ConvertUTCtoHHMM(singleStationDetails.closesAt)}`}</p>
                        </div>
                        <div className="each-availability">
                          <span>
                            <branding.icons.dashboardGas />
                          </span>
                          <p>
                            {singleStationDetails.totalChargingPoints} {t('devices')}
                          </p>
                        </div>
                        <div className="each-availability">
                          <span>
                            <branding.icons.dashboardCharge />
                          </span>
                          <p>
                            {singleStationDetails?.connectorsCount
                              ? singleStationDetails?.connectorsCount
                              : '0'}{' '}
                            {t('connector')}
                          </p>
                        </div>
                      </div>
                      <div className="view-more-button">
                        <MyButton
                          label="View more"
                          buttonType="submit"
                          disableHoverEffect
                          svgIcon={<RightArrow />}
                          onClickFunc={() => navigate('../charging-station/approved')}
                        />
                      </div>
                    </div>
                  </div>
                );
              }
              return <BatteryLoader />;
            })()}
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}

CustomDashboardMap.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
  isWidget: false,
  viewonly: false,
  staticData: null,
  showSearch: false,
};

export default CustomDashboardMap;
